// @ts-nocheck
/**
 * Widget-id → component switch. WidgetGrid calls `renderWidget(id, role)`
 * with the tile's id and the current user's role; this file returns the
 * matching component's JSX. Every ported widget from the legacy
 * DashboardPage.tsx render tree has a case here; the two prop-driven
 * ones (calls charts + agent status distribution) go through adapter
 * widgets that read shared state from DashboardDataContext.
 */
import type { WidgetId } from '../config';

import AgentBreakExceededList from './AgentBreakExceededList';
import AgentCallStats from './AgentCallStats';
import AgentLeadsDashboard from './AgentLeadsDashboard';
import AgentNotificationPanel from './AgentNotificationPanel';
import ApiUsersTable from './ApiUsersTable';
import CallCenterAdminAlert from './CallCenterAdminAlert';
// import CertExpiryChecker from './CertExpiryChecker';
import DashboardCampaignDetails from './DashboardCampaignDetails';
import DashboardIvrList from './DashboardIvrList';
import DashboardUsersCampCount from './DashboardUsersCampCount';
import DisconnectedCallsSummary from './DisconnectedCallsSummary';
import DispositionFollowUpsPanel from './DispositionFollowUpsPanel';
import OverallDemoConversionAnalytics from './OverallDemoConversionAnalytics';
import PendingDispositionsByAgent from './PendingDispositionsByAgent';
import PendingPromptsTable from './PendingPromptsTable';
import SystemMonitor from './SystemMonitor';
import TodayRunningCampaigns from './TodayRunningCampaigns';
import UserWiseCampaignSummary from './UserWiseCampaignSummary';
import CallMediaPanel from './CallMediaPanel';

// Context-backed adapters for the two widgets whose data lives in the
// shared DashboardDataContext instead of being fetched by the widget itself.
import CallsChartsWidget from './CallsChartsWidget';
import AgentStatusDistributionWidget from './AgentStatusDistributionWidget';

// KPI-strip widgets (Phase C) — all read from DashboardDataContext.
import {
  AgentsOnCallKpi,
  AgentsOnlineKpi,
  CompaniesKpi,
  LicenceAlertsKpi,
  LowBalanceAlertsKpi,
  ResellersKpi,
  RunningCampaignsKpi,
  TotalAgentsKpi,
  TotalConnectedKpi,
  TotalNumbersKpi,
} from './KpiWidgets';

export function renderWidget(id: WidgetId, _role: string): React.ReactNode {
  switch (id) {
    /* ── KPI strip ────────────────────────────────────────────── */
    case 'kpi.total-agents':
      return <TotalAgentsKpi />;
    case 'kpi.agents-online':
      return <AgentsOnlineKpi />;
    case 'kpi.agents-on-call':
      return <AgentsOnCallKpi />;
    case 'kpi.running-campaigns':
      return <RunningCampaignsKpi />;
    case 'kpi.total-numbers':
      return <TotalNumbersKpi />;
    case 'kpi.total-connected':
      return <TotalConnectedKpi />;
    case 'kpi.low-balance':
      return <LowBalanceAlertsKpi />;
    case 'kpi.licence-alerts':
      return <LicenceAlertsKpi />;
    case 'kpi.resellers':
      return <ResellersKpi />;
    case 'kpi.companies':
      return <CompaniesKpi />;

    /* ── Overview ─────────────────────────────────────────────── */
    case 'overview.users-camp-count':
      return <DashboardUsersCampCount />;
    case 'overview.disconnected-calls':
      return <DisconnectedCallsSummary />;
    case 'overview.campaign-details':
      return <DashboardCampaignDetails />;
    case 'overview.calls-charts':
      return <CallsChartsWidget />;
    case 'overview.agent-status-distribution':
      return <AgentStatusDistributionWidget />;

    /* ── Live Ops ─────────────────────────────────────────────── */
    case 'liveops.cc-admin-alert':
      return <CallCenterAdminAlert />;
    case 'liveops.system-monitor':
      return <SystemMonitor />;
    // case 'liveops.cert-expiry':
    //   return <CertExpiryChecker />;
    case 'liveops.today-running-campaigns':
      return <TodayRunningCampaigns />;
    case 'liveops.ivr-list':
      return <DashboardIvrList />;

    /* ── Agents ───────────────────────────────────────────────── */
    case 'agents.break-exceeded':
      return <AgentBreakExceededList />;
    case 'agents.notification-panel':
      return <AgentNotificationPanel />;
    case 'agents.call-stats':
      return <AgentCallStats />;
    case 'agents.leads-dashboard':
      return <AgentLeadsDashboard />;
    case 'agents.pending-dispositions':
      return <PendingDispositionsByAgent />;
    case 'agents.disposition-followups':
      return <DispositionFollowUpsPanel />;
    case 'agents.voicemails':
      return <CallMediaPanel />;

    /* ── Reports ──────────────────────────────────────────────── */
    case 'reports.demo-conversion':
      return <OverallDemoConversionAnalytics />;
    case 'reports.pending-prompts':
      return <PendingPromptsTable />;
    case 'reports.userwise-summary':
      return <UserWiseCampaignSummary />;
    case 'reports.api-users':
      return <ApiUsersTable />;

    default:
      return null;
  }
}
